import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, CheckCircle, Clock, ListChecks, RefreshCw, Target } from 'lucide-react';
import toast from 'react-hot-toast';
import { plannerAPI } from '../../services/api';

const priorityClass = {
  High: 'border-rose-400/40 bg-rose-500/10 text-rose-200',
  Medium: 'border-amber-400/40 bg-amber-500/10 text-amber-100',
  Low: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100',
};

const formatMinutes = (minutes = 0) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const TaskCard = ({ task, onComplete, isUpdating }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-purple-400/40 transition-colors"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className={`px-2.5 py-1 rounded-full border text-xs font-semibold ${priorityClass[task.priority] || priorityClass.Medium}`}>
            {task.priority}
          </span>
          <span className="text-xs text-purple-200 bg-purple-500/10 border border-purple-400/20 rounded-full px-2.5 py-1">
            {task.task_type}
          </span>
        </div>
        <h4 className={`text-white font-semibold ${task.completed ? 'line-through text-white/60' : ''}`}>{task.title}</h4>
        <p className="text-sm text-gray-300 mt-1 leading-relaxed">{task.description}</p>
        <div className="flex items-center gap-3 text-xs text-gray-400 mt-3">
          <span className="inline-flex items-center"><Clock className="w-3.5 h-3.5 mr-1" />{formatMinutes(task.duration_minutes)}</span>
          {task.related_skill ? <span className="inline-flex items-center"><Target className="w-3.5 h-3.5 mr-1" />{task.related_skill}</span> : null}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onComplete(task.id)}
        disabled={task.completed || isUpdating}
        className={`w-10 h-10 rounded-lg flex items-center justify-center border transition-colors ${
          task.completed
            ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-300'
            : 'bg-white/5 border-white/10 text-gray-300 hover:text-white hover:border-purple-400/50'
        }`}
        aria-label={task.completed ? 'Completed' : 'Mark task complete'}
      >
        <CheckCircle className="w-5 h-5" />
      </button>
    </div>
  </motion.div>
);

const DailyLearningPlannerWidget = ({ compact = false }) => {
  const [plan, setPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  const [error, setError] = useState(null);

  const loadPlan = useCallback(async (regenerate = false) => {
    try {
      regenerate ? setIsRefreshing(true) : setIsLoading(true);
      setError(null);
      const data = regenerate ? (await plannerAPI.regenerate()).today : await plannerAPI.getToday();
      setPlan(data);
      if (regenerate) toast.success('Planner regenerated');
    } catch (loadError) {
      setError(loadError.message || 'Planner unavailable');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) {
        loadPlan();
      }
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const intervalId = setInterval(refresh, 60000);

    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      clearInterval(intervalId);
    };
  }, [loadPlan]);

  const tasks = useMemo(() => (Array.isArray(plan?.tasks) ? plan.tasks : []), [plan]);
  const completed = tasks.filter((task) => task.completed).length;

  const completeTask = async (taskId) => {
    try {
      setUpdatingTaskId(taskId);
      const updated = await plannerAPI.completeTask(taskId);
      setPlan((current) => ({
        ...current,
        tasks: (current?.tasks || []).map((task) => (task.id === taskId ? updated : task)),
      }));
      toast.success('Task completed');
    } catch (completeError) {
      toast.error(completeError.message || 'Could not complete task');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-lg p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <ListChecks className="w-6 h-6 text-purple-300 mr-3" />
            Today's Focus
          </h2>
          <p className="text-gray-300 text-sm mt-1">
            {isLoading ? 'Syncing planner data...' : `${completed}/${tasks.length} tasks complete`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-gray-400">Total Time</p>
            <p className="text-white font-bold">{formatMinutes(plan?.total_time || 0)}</p>
          </div>
          <button
            type="button"
            onClick={() => loadPlan(true)}
            disabled={isRefreshing}
            className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-center disabled:opacity-60"
            aria-label="Regenerate planner"
          >
            <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error ? (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4 text-rose-100 text-sm">{error}</div>
      ) : isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((item) => <div key={item} className="h-24 bg-white/5 border border-white/10 rounded-lg animate-pulse" />)}
        </div>
      ) : tasks.length > 0 ? (
        <div className={compact ? 'space-y-3' : 'grid grid-cols-1 gap-3'}>
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} onComplete={completeTask} isUpdating={updatingTaskId === task.id} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-300">
          <BookOpen className="w-10 h-10 mx-auto mb-3 text-purple-300" />
          Your planner will appear after profile, role, or roadmap data is available.
        </div>
      )}
    </motion.section>
  );
};

export default DailyLearningPlannerWidget;
